import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Alert,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import AddIcon from "@mui/icons-material/Add";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import DataTable from "datatables.net-bs5";
import $ from "jquery";
import "datatables.net-bs5/css/dataTables.bootstrap5.min.css";
import { customerTypeAPI } from "../services/api";
import Swal from "sweetalert2";
import "./RaiseComplaint.css";
import "./CustomerTypeManagement.css";

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${day}-${month}-${year} ${hours}:${minutes}`;
};

const CustomerTypeManagement = () => {
  const [customerTypes, setCustomerTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCustomerType, setEditingCustomerType] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [isActiveFilter, setIsActiveFilter] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize] = useState(50);
  const [totalRecords, setTotalRecords] = useState(0);
  const [error, setError] = useState(null);
  const tableRef = useRef(null);
  const dataTableInstanceRef = useRef(null);

  const currentUser = (() => {
    try {
      const u = localStorage.getItem("user");
      return u ? JSON.parse(u) : null;
    } catch (e) {
      return null;
    }
  })();

  useEffect(() => {
    fetchCustomerTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber, searchText, isActiveFilter]);

  useEffect(() => {
    if (!tableRef.current) return;

    // Destroy previous instance
    if (dataTableInstanceRef.current) {
      dataTableInstanceRef.current.destroy();
      dataTableInstanceRef.current = null;
    }

    // Initialize DataTable plugin
    try {
      if (!$.fn.DataTable) {
        DataTable($);
      }

      dataTableInstanceRef.current = $(tableRef.current).DataTable({
        data: customerTypes,
        paging: true,
        searching: true,
        info: true,
        lengthChange: true,
        lengthMenu: [5, 10, 25, 50, 100],
        pageLength: 10,
        dom: "lfrtip",
        ordering: true,
        destroy: true,
        scrollX: true,
        autoWidth: false,
        columns: [
          {
            title: "SNo.",
            data: null,
            render: (d, type, row, meta) =>
              (pageNumber - 1) * pageSize + meta.row + 1,
            className: "text-center",
          },
          { title: "Customer Type Code", data: "customerTypeCode" },
          { title: "Type Name", data: "typeName" },
          {
            title: "Status",
            data: null,
            render: (d) =>
              d.isActive
                ? '<span class="badge bg-success">Active</span>'
                : '<span class="badge bg-secondary">Inactive</span>',
          },
          {
            title: "Created By",
            data: "createdByName",
            render: (d) => d || "-",
          },
          {
            title: "Created On",
            data: "createdOn",
            render: (d) => formatDateTime(d),
          },
          {
            title: "Modified By",
            data: "modifiedByName",
            render: (d) => d || "-",
          },
          {
            title: "Modified On",
            data: "modifiedOn",
            render: (d) => formatDateTime(d),
          },
          {
            title: "Action",
            data: null,
            orderable: false,
            searchable: false,
            render: (d) =>
              `<div class="d-flex justify-content-center gap-2">
                <button class="btn btn-sm edit-ct" data-code="${d.customerTypeCode}" 
                  title="Edit"
                  style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); 
                         border: none; 
                         color: white; 
                         padding: 8px 10px; 
                         border-radius: 6px; 
                         box-shadow: 0 2px 8px rgba(102, 126, 234, 0.4);
                         transition: all 0.3s ease;
                         font-weight: 500;
                         display: flex;
                         align-items: center;
                         justify-content: center;
                         min-width: 36px;
                         height: 36px;">
                  <i class="bi bi-pencil-fill"></i>
                </button>
                <button class="btn btn-sm delete-ct" data-code="${d.customerTypeCode}"
                  title="Delete"
                  style="background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%); 
                         border: none; 
                         color: white; 
                         padding: 8px 10px; 
                         border-radius: 6px; 
                         box-shadow: 0 2px 8px rgba(245, 87, 108, 0.4);
                         transition: all 0.3s ease;
                         font-weight: 500;
                         display: flex;
                         align-items: center;
                         justify-content: center;
                         min-width: 36px;
                         height: 36px;">
                  <i class="bi bi-trash-fill"></i>
                </button>
              </div>`,
          },
        ],
        initComplete: function (settings, json) {
          try {
            // Wrap dt-length and dt-search into a single top filter wrapper
            const wrapper = $(tableRef.current).closest(
              ".dataTables_wrapper, .dt-container"
            );
            if (wrapper.length > 0) {
              const lengthEl = wrapper.find(".dt-length");
              const searchEl = wrapper.find(".dt-search");
              if (lengthEl.length > 0 || searchEl.length > 0) {
                let topWrap = wrapper.find(".dtFltrTop");
                if (topWrap.length === 0) {
                  topWrap = $("<div class='dtFltrTop d-flex flex-wrap'></div>");
                  wrapper.prepend(topWrap);
                }
                lengthEl.each(function () {
                  $(this).appendTo(topWrap);
                });
                searchEl.each(function () {
                  $(this).appendTo(topWrap);
                });
              }

              // Wrap dt-info and dt-paging into a single bottom filter wrapper
              const infoEl = wrapper.find(".dt-info");
              const pagingEl = wrapper.find(".dt-paging");
              if (infoEl.length > 0 || pagingEl.length > 0) {
                let bottomWrap = wrapper.find(".dtFltrBotm");
                if (bottomWrap.length === 0) {
                  bottomWrap = $(
                    "<div class='dtFltrBotm d-flex flex-wrap'></div>"
                  );
                  wrapper.append(bottomWrap);
                }
                infoEl.each(function () {
                  $(this).appendTo(bottomWrap);
                });
                pagingEl.each(function () {
                  $(this).appendTo(bottomWrap);
                });
              }
            }

            // Wrap dt-scroll with an inner wrapper and ensure outer wrapper has dtScrlWrpr
            const dtScrollDiv = $(tableRef.current).closest(".dt-scroll");
            if (dtScrollDiv.length > 0) {
              if (!dtScrollDiv.parent().hasClass("dtScrlWrprInner")) {
                dtScrollDiv.wrap('<div class="dtScrlWrprInner"></div>');
              }
              const outer = dtScrollDiv.parent().parent();
              if (outer.length > 0) outer.addClass("dtScrlWrpr");
            } else {
              const container = $(tableRef.current).closest(
                ".table-responsive"
              );
              if (container.length > 0) {
                const dtScroll = container.find(".dt-scroll");
                if (dtScroll.length > 0) {
                  dtScroll.each(function () {
                    const $this = $(this);
                    if (!$this.parent().hasClass("dtScrlWrprInner")) {
                      $this.wrap('<div class="dtScrlWrprInner"></div>');
                    }
                    const outer = $this.parent().parent();
                    if (outer.length > 0) outer.addClass("dtScrlWrpr");
                  });
                }
              }
            }
          } catch (error) {
            console.error("Error in initComplete:", error);
          }
        },
      });

      // Add hover effects for glossy buttons
      $(tableRef.current)
        .off("mouseenter", ".edit-ct, .delete-ct")
        .on("mouseenter", ".edit-ct, .delete-ct", function () {
          $(this).css({
            transform: "translateY(-2px)",
            boxShadow: $(this).hasClass("edit-ct")
              ? "0 4px 12px rgba(102, 126, 234, 0.6)"
              : "0 4px 12px rgba(245, 87, 108, 0.6)",
          });
        })
        .off("mouseleave", ".edit-ct, .delete-ct")
        .on("mouseleave", ".edit-ct, .delete-ct", function () {
          $(this).css({
            transform: "translateY(0)",
            boxShadow: $(this).hasClass("edit-ct")
              ? "0 2px 8px rgba(102, 126, 234, 0.4)"
              : "0 2px 8px rgba(245, 87, 108, 0.4)",
          });
        })
        .off("mousedown", ".edit-ct, .delete-ct")
        .on("mousedown", ".edit-ct, .delete-ct", function () {
          $(this).css({ transform: "translateY(0)" });
        })
        .off("mouseup", ".edit-ct, .delete-ct")
        .on("mouseup", ".edit-ct, .delete-ct", function () {
          $(this).css({ transform: "translateY(-2px)" });
        });

      // Delegated handlers for edit/delete
      $(tableRef.current)
        .off("click", ".edit-ct")
        .on("click", ".edit-ct", function () {
          const code = $(this).data("code");
          const ct = customerTypes.find((x) => x.customerTypeCode === code);
          if (ct) openEditDialog(ct);
        });

      $(tableRef.current)
        .off("click", ".delete-ct")
        .on("click", ".delete-ct", function () {
          const code = $(this).data("code");
          if (code) {
            handleDelete(code);
          }
        });
    } catch (err) {
      console.error("DataTable init error", err);
    }

    return () => {
      if (dataTableInstanceRef.current) {
        dataTableInstanceRef.current.destroy();
        dataTableInstanceRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerTypes, pageNumber, pageSize]);

  const fetchCustomerTypes = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await customerTypeAPI.getAll({
        searchText: searchText || undefined,
        isActive: isActiveFilter,
        pageNumber,
        pageSize,
      });

      if (response && response.success && response.data) {
        const data =
          response.data.customerTypes || response.data.CustomerTypes || [];
        setCustomerTypes(data);
        setTotalRecords(
          response.data.totalRecords || response.data.TotalRecords || 0
        );
      } else {
        setCustomerTypes([]);
        setTotalRecords(0);
      }
    } catch (e) {
      console.error("Error fetching customer types:", e);
      setError("Failed to load customer types. Please try again.");
      setCustomerTypes([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingCustomerType({
      customerTypeCode: "",
      typeName: "",
      isActive: true,
    });
    setDialogOpen(true);
  };

  const openEditDialog = (customerType) => {
    setEditingCustomerType({ ...customerType });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!editingCustomerType) return;

    if (!editingCustomerType.customerTypeCode?.trim()) {
      Swal.fire("Error", "Customer Type Code is required", "error");
      return;
    }

    if (!editingCustomerType.typeName?.trim()) {
      Swal.fire("Error", "Type Name is required", "error");
      return;
    }

    try {
      const createdBy = currentUser?.userIdCode || "SYSADMIN001";
      let response;

      if (
        editingCustomerType.customerTypeCode &&
        customerTypes.some(
          (ct) => ct.customerTypeCode === editingCustomerType.customerTypeCode
        )
      ) {
        // Update existing
        response = await customerTypeAPI.update(
          editingCustomerType.customerTypeCode,
          {
            typeName: editingCustomerType.typeName,
            isActive: editingCustomerType.isActive,
          },
          createdBy
        );
      } else {
        // Create new
        response = await customerTypeAPI.create(
          {
            customerTypeCode: editingCustomerType.customerTypeCode,
            typeName: editingCustomerType.typeName,
            isActive: editingCustomerType.isActive,
          },
          createdBy
        );
      }

      if (response && response.success) {
        Swal.fire(
          "Success",
          response.message || "Customer type saved successfully",
          "success"
        );
        setDialogOpen(false);
        setEditingCustomerType(null);
        fetchCustomerTypes();
      } else {
        Swal.fire(
          "Error",
          response.message || "Failed to save customer type",
          "error"
        );
      }
    } catch (e) {
      console.error("Error saving customer type:", e);
      Swal.fire(
        "Error",
        e.response?.data?.message ||
          "Failed to save customer type. Please try again.",
        "error"
      );
    }
  };

  const handleDelete = async (customerTypeCode) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const modifiedBy = currentUser?.userIdCode || "SYSADMIN001";
        const response = await customerTypeAPI.delete(
          customerTypeCode,
          modifiedBy
        );

        if (response && response.success) {
          Swal.fire(
            "Deleted!",
            response.message || "Customer type has been deleted.",
            "success"
          );
          fetchCustomerTypes();
        } else {
          Swal.fire(
            "Error",
            response.message || "Failed to delete customer type",
            "error"
          );
        }
      } catch (e) {
        console.error("Error deleting customer type:", e);
        Swal.fire(
          "Error",
          e.response?.data?.message ||
            "Failed to delete customer type. Please try again.",
          "error"
        );
      }
    }
  };

  const handleSearch = () => {
    setPageNumber(1);
    fetchCustomerTypes();
  };

  const handleReset = () => {
    setSearchText("");
    setIsActiveFilter(null);
    setPageNumber(1);
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Typography
          variant="h4"
          component="h1"
          className="service-request-heading"
          sx={{ color: "#333333", fontWeight: 600 }}
        >
          Customer Type Management
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filter Panel */}
      <div className="card-panel mb-3 filter-panel">
        <div className="section-heading d-flex align-items-center gap-2 mb-3">
          <SearchIcon fontSize="small" className="text-muted" />
          <span>Filter Customer Types</span>
        </div>

        <div className="container-fluid">
          <div className="row g-3">
            <div className="col-12 col-md-6 col-lg-4">
              <label className="input-label">Search</label>
              <TextField
                placeholder="Search by code or name..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                fullWidth
                size="small"
                className="form-control-light"
                InputProps={{
                  startAdornment: (
                    <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#f7fcff",
                    border: "1px solid #e3ebef",
                    borderRadius: "10px",
                    "& fieldset": {
                      border: "none",
                    },
                    "&:hover fieldset": {
                      border: "none",
                    },
                    "&.Mui-focused fieldset": {
                      border: "1px solid #b8c2cc",
                    },
                  },
                }}
              />
            </div>

            <div className="col-12 col-md-6 col-lg-3">
              <label className="input-label">Status</label>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isActiveFilter === true}
                    onChange={(e) =>
                      setIsActiveFilter(e.target.checked ? true : null)
                    }
                  />
                }
                label="Active Only"
                sx={{ mt: 1.5 }}
              />
            </div>

            <div className="col-12 col-md-6 col-lg-3">
              <label
                className="input-label d-block"
                style={{ visibility: "hidden" }}
              >
                Actions
              </label>
              <div className="d-flex gap-2 filter-buttons">
                <Button
                  variant="contained"
                  onClick={handleSearch}
                  startIcon={<SearchIcon />}
                  sx={{
                    flex: 1,
                    background:
                      "linear-gradient(135deg, #7a00f5  0%, #0f00f5 100%)",
                    border: "none",
                    color: "white",
                    fontWeight: 600,
                    boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
                    borderRadius: "4px",
                    textTransform: "none",
                    padding: "8px 24px",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #0f00f5 0%, #0f00f5 100%)",
                      boxShadow: "0 6px 20px rgba(102, 126, 234, 0.6)",
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  Search
                </Button>
                <Button
                  variant="contained"
                  onClick={handleReset}
                  sx={{
                    background:
                      "linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%)",
                    border: "none",
                    color: "white",
                    fontWeight: 600,
                    boxShadow: "0 4px 15px rgba(132, 250, 176, 0.4)",
                    borderRadius: "4px",
                    textTransform: "none",
                    padding: "8px 16px",
                    minWidth: "auto",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%)",
                      boxShadow: "0 6px 20px rgba(132, 250, 176, 0.6)",
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s ease",
                  }}
                  title="Reset"
                >
                  <RestartAltIcon fontSize="small" />
                </Button>
              </div>
            </div>

            <div className="col-12 col-md-6 col-lg-2">
              <label
                className="input-label d-block"
                style={{ visibility: "hidden" }}
              >
                Add
              </label>
              <Button
                variant="contained"
                onClick={openCreateDialog}
                startIcon={<AddIcon />}
                fullWidth
                sx={{
                  background:
                    "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                  border: "none",
                  color: "white",
                  fontWeight: 600,
                  boxShadow: "0 4px 15px rgba(245, 87, 108, 0.4)",
                  borderRadius: "8px",
                  textTransform: "none",
                  padding: "8px 20px",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                    boxShadow: "0 6px 20px rgba(245, 87, 108, 0.6)",
                    transform: "translateY(-2px)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                Add Customer Type
              </Button>
            </div>
          </div>
        </div>
      </div>

      <Paper
        sx={{
          p: 0,
          backgroundColor: "transparent",
          boxShadow: "none",
          border: "none",
        }}
      >
        <div className="card-panel mt-0">
          <div className="table-responsive">
            <table
              ref={tableRef}
              className="table table-striped table-bordered complaint-table custmTblLst"
              style={{ width: "100%" }}
            />
          </div>
        </div>
      </Paper>

      {loading && customerTypes.length === 0 && (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography>Loading...</Typography>
        </Box>
      )}

      {/* Create/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingCustomerType &&
          customerTypes.some(
            (ct) => ct.customerTypeCode === editingCustomerType.customerTypeCode
          )
            ? "Edit Customer Type"
            : "Create Customer Type"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <TextField
              label="Customer Type Code"
              value={editingCustomerType?.customerTypeCode || ""}
              onChange={(e) =>
                setEditingCustomerType({
                  ...editingCustomerType,
                  customerTypeCode: e.target.value.toUpperCase(),
                })
              }
              disabled={
                editingCustomerType &&
                customerTypes.some(
                  (ct) =>
                    ct.customerTypeCode === editingCustomerType.customerTypeCode
                )
              }
              required
              fullWidth
            />
            <TextField
              label="Type Name"
              value={editingCustomerType?.typeName || ""}
              onChange={(e) =>
                setEditingCustomerType({
                  ...editingCustomerType,
                  typeName: e.target.value,
                })
              }
              required
              fullWidth
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={editingCustomerType?.isActive ?? true}
                  onChange={(e) =>
                    setEditingCustomerType({
                      ...editingCustomerType,
                      isActive: e.target.checked,
                    })
                  }
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDialogOpen(false)}
            sx={{
              background: "linear-gradient(135deg, #e0e0e0 0%, #bdbdbd 100%)",
              border: "none",
              color: "white",
              fontWeight: 600,
              boxShadow: "0 4px 15px rgba(189, 189, 189, 0.4)",
              borderRadius: "8px",
              textTransform: "none",
              padding: "8px 20px",
              "&:hover": {
                background: "linear-gradient(135deg, #e0e0e0 0%, #bdbdbd 100%)",
                boxShadow: "0 6px 20px rgba(189, 189, 189, 0.6)",
                transform: "translateY(-2px)",
              },
              transition: "all 0.3s ease",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            sx={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              border: "none",
              color: "white",
              fontWeight: 600,
              boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
              borderRadius: "8px",
              textTransform: "none",
              padding: "8px 24px",
              "&:hover": {
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                boxShadow: "0 6px 20px rgba(102, 126, 234, 0.6)",
                transform: "translateY(-2px)",
              },
              transition: "all 0.3s ease",
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomerTypeManagement;
